<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <leave-conflict
      v-if="showLeaveConflict"
      :workshift-exceeded="isWorkShiftExceeded"
      :data="leaveConflictData"
    ></leave-conflict>
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.apply_leave') }}
      </oxd-text>

      <oxd-divider />

      <oxd-text v-if="!isLoading && leaveTypes.length === 0" type="subtitle-2">
        {{ $t('leave.no_leave_types_with_leave_balance') }}
      </oxd-text>

      <oxd-form
        v-else
        ref="formRef"
        :loading="isLoading"
        @submit-valid="onSave"
      >
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leave.type"
                type="select"
                :rules="rules.type"
                :options="leaveTypes"
                :label="$t('leave.leave_type')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="!leave.type?.hideLeaveBalanceOnApply">
              <leave-balance
                v-model:noteLeave="noteLeave"
                :leave-data="leave"
              ></leave-balance>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="leave.type && noteLeave">
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="noteLeave"
                type="textarea"
                :label="$t('general.note')"
                disabled
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="leave.fromDate"
                :label="$t('general.from_date')"
                :rules="rules.fromDate"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="leave.toDate"
                :label="$t('general.to_date')"
                :rules="rules.toDate"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <!-- Single Day|Duration -->
        <oxd-form-row v-if="appliedLeaveDuration == 1">
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <leave-duration-input
              v-model:duration="leave.duration.type"
              v-model:fromTime="leave.duration.fromTime"
              v-model:toTime="leave.duration.toTime"
              :label="$t('general.duration')"
              :work-shift="workShift"
            ></leave-duration-input>
          </oxd-grid>
        </oxd-form-row>
        <!-- Single Day|Duration -->

        <!-- Partial Day|Duration -->
        <oxd-form-row v-if="appliedLeaveDuration > 1">
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leave.partialOptions"
                type="select"
                :label="$t('leave.partial_days')"
                :options="partialOptions"
              />
            </oxd-grid-item>
            <leave-duration-input
              v-if="showDuration"
              v-model:duration="leave.duration.type"
              v-model:fromTime="leave.duration.fromTime"
              v-model:toTime="leave.duration.toTime"
              :partial="true"
              :label="$t('general.duration')"
              :work-shift="workShift"
            ></leave-duration-input>
            <leave-duration-input
              v-if="showStartDay"
              v-model:duration="leave.duration.type"
              v-model:fromTime="leave.duration.fromTime"
              v-model:toTime="leave.duration.toTime"
              :partial="true"
              :label="$t('leave.start_day')"
              :work-shift="workShift"
            ></leave-duration-input>
            <leave-duration-input
              v-if="showEndDay"
              v-model:duration="leave.endDuration.type"
              v-model:fromTime="leave.endDuration.fromTime"
              v-model:toTime="leave.endDuration.toTime"
              :partial="true"
              :label="$t('leave.end_day')"
              :work-shift="workShift"
            ></leave-duration-input>
          </oxd-grid>
        </oxd-form-row>

        <!-- Medical certificate: prior sick in same month OR leave spans more than one day (see MyLeaveRequestAPI) -->
        <oxd-form-row
          v-if="
            leave.type &&
            leave.type.id === sickLeaveTypeId &&
            appliedLeaveDuration >= 1 &&
            isSickDocMandatory
          "
        >
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-group
                label="DocSick Letter/Medical Certificate*"
                required
              >
                <photo-input v-model="leave.documents" :single="true" />
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <!-- Partial Day|Duration -->

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leave.comment"
                type="textarea"
                :label="$t('general.comments')"
                :rules="rules.comment"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <leave-supervisor-autocomplete
                v-model="leave.pic"
                :emp-number="empNumber"
                :label="picLabel"
                :required="hasSupervisors"
                :rules="picRules"
                @supervisors-loaded="onSupervisorsLoaded"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <submit-button :label="$t('general.apply')" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {
  required,
  validDateFormat,
  validSelection,
  shouldNotExceedCharLength,
  shouldNotLessThanCharLength,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
} from '@/core/util/validation/rules';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import {
  diffInDays,
  formatDate,
  parseDate,
} from '@ohrm/core/util/helper/datefns';
import {APIService} from '@ohrm/core/util/services/api.service';
import LeaveDurationInput from '@/orangehrmLeavePlugin/components/LeaveDurationInput';
import LeaveBalance from '@/orangehrmLeavePlugin/components/LeaveBalance';
import LeaveConflict from '@/orangehrmLeavePlugin/components/LeaveConflict';
import useLeaveValidators from '@/orangehrmLeavePlugin/util/composable/useLeaveValidators';
import useForm from '@ohrm/core/util/composable/useForm';
import useDateFormat from '@/core/util/composable/useDateFormat';
import PhotoInput from '@/orangehrmBuzzPlugin/components/PhotoInput';
import LeaveSupervisorAutocomplete from '@/orangehrmLeavePlugin/components/LeaveSupervisorAutocomplete';
import {ref} from 'vue';

/** Must match `MyLeaveRequestAPI::LEAF_TYPE_ID` (sick leave). */
const SICK_LEAVE_TYPE_ID = 3;

const leaveModel = {
  type: null,
  pic: null,
  fromDate: null,
  toDate: null,
  comment: '',
  documents: [],
  partialOptions: null,
  duration: {
    type: null,
    fromTime: null,
    toTime: null,
  },
  endDuration: {
    type: null,
    fromTime: null,
    toTime: null,
  },
};

export default {
  name: 'LeaveApply',

  components: {
    'leave-duration-input': LeaveDurationInput,
    'leave-balance': LeaveBalance,
    'leave-conflict': LeaveConflict,
    'photo-input': PhotoInput,
    'leave-supervisor-autocomplete': LeaveSupervisorAutocomplete,
  },

  props: {
    workShift: {
      type: Object,
      default: () => ({}),
    },
    empNumber: {
      type: Number,
      required: true,
    },
  },

  setup() {
    const noteLeave = ref('');
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-requests',
    );
    const {serializeBody, validateOverlapLeaves} = useLeaveValidators(http);
    const {formRef, reset} = useForm();
    const {userDateFormat} = useDateFormat();
    const onNoteLeave = (val) => {
      noteLeave.value = val;
    };
    return {
      http,
      reset,
      formRef,
      serializeBody,
      userDateFormat,
      validateOverlapLeaves,
      onNoteLeave,
      noteLeave,
    };
  },

  data() {
    return {
      isLoading: false,
      leave: {...leaveModel},
      rules: {
        type: [required],
        fromDate: [
          required,
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.leave.toDate,
            this.$t('general.from_date_should_be_before_to_date'),
            {allowSameDate: true},
          ),
        ],
        toDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.leave.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
        comment: [
          required,
          shouldNotLessThanCharLength(11),
          shouldNotExceedCharLength(250),
        ],
      },
      hasSupervisors: false,
      partialOptions: [
        {id: 1, label: this.$t('leave.all_days'), key: 'all'},
        {id: 2, label: this.$t('leave.start_day_only'), key: 'start'},
        {id: 3, label: this.$t('leave.end_day_only'), key: 'end'},
        {id: 4, label: this.$t('leave.start_and_end_day'), key: 'start_end'},
      ],
      showLeaveConflict: false,
      isWorkShiftExceeded: false,
      leaveConflictData: null,
      yearsArray: [...yearRange()],
      leaveTypes: [],
      sickLeaveTypeId: SICK_LEAVE_TYPE_ID,
      sickLeavePriorCount: 0,
    };
  },

  computed: {
    isSickDocMandatory() {
      if (this.sickLeavePriorCount >= 1) {
        return true;
      }
      const d = this.appliedLeaveDuration;
      return typeof d === 'number' && d > 1;
    },
    appliedLeaveDuration() {
      return diffInDays(this.leave.fromDate, this.leave.toDate);
    },
    showDuration() {
      const id = this.leave.partialOptions?.id;
      return id && id === 1;
    },
    showStartDay() {
      const id = this.leave.partialOptions?.id;
      return id && (id === 2 || id === 4);
    },
    showEndDay() {
      const id = this.leave.partialOptions?.id;
      return id && (id === 3 || id === 4);
    },
    picLabel() {
      const label = this.$t('leave.person_in_charge');
      return this.hasSupervisors ? label : `${label} (Optional)`;
    },
    picRules() {
      return this.hasSupervisors
        ? [required, validSelection]
        : [validSelection];
    },
  },

  watch: {
    appliedLeaveDuration: function (duration) {
      if (duration === 1) {
        this.leave.duration.type = {id: 1, label: 'Full Day', key: 'full_day'};
      } else {
        this.leave.duration.type = null;
      }
    },
    'leave.fromDate': function (fromDate) {
      const toDate = this.leave.toDate;
      if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
        this.leave.toDate = fromDate;
      }
      this.refreshSickLeavePriorCount();
    },
    'leave.toDate': function () {
      this.refreshSickLeavePriorCount();
    },
    'leave.type': function () {
      if (this.leave.type?.hideLeaveBalanceOnApply) {
        this.noteLeave = '';
        this.onNoteLeave('');
      }
      this.refreshSickLeavePriorCount();
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .request({
        method: 'GET',
        url: '/api/v2/leave/leave-types/eligible',
      })
      .then((response) => {
        const {data} = response.data;
        this.leaveTypes = data.map((item) => {
          return {
            id: item.id,
            label: item.name,
            hideLeaveBalanceOnApply: !!item.hideLeaveBalanceOnApply,
          };
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSupervisorsLoaded(count) {
      this.hasSupervisors = count > 0;
    },
    toApiYyyyMmDd(fromDateVal) {
      if (!fromDateVal) {
        return null;
      }
      if (typeof fromDateVal.format === 'function') {
        return fromDateVal.format('YYYY-MM-DD');
      }
      if (fromDateVal instanceof Date) {
        const ymd = formatDate(fromDateVal, 'yyyy-MM-dd');
        return ymd ?? null;
      }
      if (typeof fromDateVal === 'string') {
        let parsed = parseDate(fromDateVal, this.userDateFormat);
        if (!parsed) {
          parsed = parseDate(fromDateVal, 'yyyy-MM-dd');
        }
        if (parsed) {
          const ymd = formatDate(parsed, 'yyyy-MM-dd');
          if (ymd) {
            return ymd;
          }
        }
      }
      return null;
    },
    getMonthRangeFromApiDate(apiYmd) {
      const parsed = parseDate(apiYmd, 'yyyy-MM-dd');
      if (!parsed) {
        return null;
      }
      const y = parsed.getFullYear();
      const m = parsed.getMonth() + 1;
      const last = new Date(y, m, 0).getDate();
      const pad = (n) => String(n).padStart(2, '0');
      return {
        fromDate: `${y}-${pad(m)}-01`,
        toDate: `${y}-${pad(m)}-${pad(last)}`,
      };
    },
    refreshSickLeavePriorCount() {
      if (this.leave.type?.id !== SICK_LEAVE_TYPE_ID || !this.leave.fromDate) {
        this.sickLeavePriorCount = 0;
        return;
      }
      const apiFrom = this.toApiYyyyMmDd(this.leave.fromDate);
      if (!apiFrom) {
        this.sickLeavePriorCount = 0;
        return;
      }
      const range = this.getMonthRangeFromApiDate(apiFrom);
      if (!range) {
        this.sickLeavePriorCount = 0;
        return;
      }
      this.http
        .getAll({
          leaveTypeId: SICK_LEAVE_TYPE_ID,
          fromDate: range.fromDate,
          toDate: range.toDate,
          statuses: [1, 2, 3],
          limit: 1,
        })
        .then((res) => {
          this.sickLeavePriorCount = res.data?.meta?.total ?? 0;
        })
        .catch(() => {
          this.sickLeavePriorCount = 0;
        });
    },
    onSave() {
      if (
        this.leave.type?.id === this.sickLeaveTypeId &&
        this.isSickDocMandatory &&
        (!this.leave.documents || this.leave.documents.length === 0)
      ) {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message:
            'DocSick Letter/Medical Certificate is required when sick leave spans more than one day, or when you already have sick leave in the same calendar month.',
        });
        return;
      }
      this.isLoading = true;
      this.showLeaveConflict = false;
      this.leaveConflictData = null;

      this.validateOverlapLeaves(this.leave)
        .then(({isConflict, isOverWorkshift, data}) => {
          if (isConflict) {
            this.leaveConflictData = data;
            this.showLeaveConflict = true;
            this.isWorkShiftExceeded = isOverWorkshift;
            return Promise.reject();
          }
          // console.log(this.leave);
          return this.http.create(this.serializeBody(this.leave));
        })
        .then(() => {
          this.$toast.saveSuccess();
          this.reset();
        })
        .catch(() => {
          this.showLeaveConflict &&
            this.$toast.warn({
              title: this.$t('general.warning'),
              message: this.$t('leave.failed_to_submit_check_overlapping'),
            });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
